import { Injectable } from '@angular/core';
import { NgForage } from 'ngforage';
import {
  BehaviorSubject,
  interval,
  Subject,
  Subscription,
  merge,
  Observable
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import ErrorHelper from 'src/app/helpers/error-helper';
import Branch from 'src/app/model/branch/branch';
import { ConnectionStatusService } from '../connection-status.service';
import { BranchControllerRequestService } from './branch-controller-request.service';

@Injectable({
  providedIn: 'root'
})
// TODO generalize this class if more locally stored entities are needed
export class OfflineBackedBranchChoicesService {
  private branchChoices = new BehaviorSubject<Branch[]>([]);

  private readonly BRANCHES_STORAGE_KEY = 'branches';
  private readonly RETRY_INTERVAL_MS = 60000; // one minute

  private onSuccess = new Subject<void>();
  private connectionRestoredTrigger = new Subject<void>();
  private connectionRestoredTriggerSubscription: Subscription;

  private online = true;

  constructor(
    private branchControllerRequestService: BranchControllerRequestService,
    private connectionStatusService: ConnectionStatusService,
    private ngForage: NgForage
  ) {
    this.doInitialize();
  }

  private async doInitialize(): Promise<void> {
    const offlineStoredBranches: Branch[] = await this.ngForage.getItem(
      this.BRANCHES_STORAGE_KEY
    );
    if (offlineStoredBranches != null) {
      this.branchChoices.next(offlineStoredBranches);
    }

    this.initializeGetFromOnline();
  }

  private initializeGetFromOnline(): void {
    this.onSuccess.subscribe(() => {
      this.connectionRestoredTriggerSubscription.unsubscribe();
      this.connectionRestoredTrigger.complete();

      this.connectionRestoredTriggerSubscription = null;
      this.connectionRestoredTrigger = null;
    });

    this.connectionRestoredTriggerSubscription = this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doTrigger = !this.online && online;
        this.online = online;
        if (doTrigger) {
          this.connectionRestoredTrigger.next(null);
        }
      });

    merge(this.connectionRestoredTrigger, interval(this.RETRY_INTERVAL_MS))
      .pipe(takeUntil(this.onSuccess))
      .subscribe(() => this.tryGetFromOnline());

    this.connectionRestoredTrigger.next(null);
  }

  private async tryGetFromOnline(): Promise<void> {
    try {
      if (this.online) {
        const page = await this.branchControllerRequestService.getPaged();
        await this.ngForage.setItem(this.BRANCHES_STORAGE_KEY, page.items);
        this.branchChoices.next(page.items);
        this.onSuccess.next();
        this.onSuccess.complete();
        this.onSuccess = null;
      }
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err)) {
        throw err;
      }
    }
  }

  public getBranchChoicesSubscribable(): Observable<Branch[]> {
    return this.branchChoices;
  }
}
